import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  IndexTable,
  Badge,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Banner,
  useIndexResourceState,
  InlineStack,
  Box,
} from '@shopify/polaris';
import {
  faqService,
  type FAQ,
  type CreateFAQInput,
  type ContentStatus,
} from '../../services/content.service';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

export function FAQManagementPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [categories, setCategories] = useState<Array<{ category: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [category, setCategory] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [status, setStatus] = useState<ContentStatus>('ACTIVE');

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(faqs as unknown as {[key: string]: unknown}[]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [faqData, categoryData] = await Promise.all([
        faqService.list({ limit: 100 }),
        faqService.getCategories(),
      ]);
      setFaqs(faqData.data);
      setCategories(categoryData);
    } catch (err) {
      setError('Failed to load FAQs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setCategory('');
    setQuestion('');
    setAnswer('');
    setDisplayOrder('0');
    setStatus('ACTIVE');
    setEditingFaq(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (faq: FAQ) => {
    setEditingFaq(faq);
    setCategory(faq.category);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setDisplayOrder(faq.displayOrder.toString());
    setStatus(faq.status);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!question.trim() || !answer.trim() || !category.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const data: CreateFAQInput = {
        category: category.trim(),
        question: question.trim(),
        answer: answer.trim(),
        displayOrder: parseInt(displayOrder) || 0,
        status,
      };

      if (editingFaq) {
        await faqService.update(editingFaq.id, data);
      } else {
        await faqService.create(data);
      }

      setShowModal(false);
      resetForm();
      await loadData();
    } catch (err) {
      setError('Failed to save FAQ');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await faqService.delete(id);
      await loadData();
    } catch (err) {
      setError('Failed to delete FAQ');
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedResources.length} FAQs?`)) return;

    try {
      await Promise.all(selectedResources.map((id) => faqService.delete(id)));
      clearSelection();
      await loadData();
    } catch (err) {
      setError('Failed to delete FAQs');
      console.error(err);
    }
  };

  const rowMarkup = faqs.map((faq, index) => (
    <IndexTable.Row
      id={faq.id}
      key={faq.id}
      selected={selectedResources.includes(faq.id)}
      position={index}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {faq.question}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge>{faq.category}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" as="span">
          {faq.answer.slice(0, 100)}
          {faq.answer.length > 100 ? '...' : ''}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {faq.displayOrder}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={faq.status === 'ACTIVE' ? 'success' : undefined}>
          {faq.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => openEditModal(faq)}>
            Edit
          </Button>
          <Button size="slim" tone="critical" onClick={() => handleDelete(faq.id)}>
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const bulkActions = [
    {
      content: 'Delete selected',
      onAction: handleBulkDelete,
      destructive: true,
    },
  ];

  // Unique categories for the select dropdown
  const categoryOptions = [
    { label: 'Type a new category or select existing', value: '' },
    ...categories.map((c) => ({ label: `${c.category} (${c.count})`, value: c.category })),
  ];

  return (
    <Page
      title="FAQ Management"
      primaryAction={{
        content: 'Add FAQ',
        onAction: openCreateModal,
      }}
    >
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Box>
      )}

      <Card padding="0">
        <IndexTable
          resourceName={{ singular: 'FAQ', plural: 'FAQs' }}
          itemCount={faqs.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          bulkActions={bulkActions}
          headings={[
            { title: 'Question' },
            { title: 'Category' },
            { title: 'Answer' },
            { title: 'Order' },
            { title: 'Status' },
            { title: 'Actions' },
          ]}
          loading={loading}
        >
          {rowMarkup}
        </IndexTable>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingFaq ? 'Edit FAQ' : 'Add FAQ'}
        primaryAction={{
          content: editingFaq ? 'Save Changes' : 'Create FAQ',
          loading: saving,
          onAction: handleSave,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowModal(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Category"
              options={categoryOptions}
              onChange={setCategory}
              value={category}
              helpText="Select an existing category or type a new one below"
            />
            <TextField
              label="Category (custom)"
              value={category}
              onChange={setCategory}
              autoComplete="off"
              placeholder="e.g., Ordering, Shipping, Returns"
            />
            <TextField
              label="Question"
              value={question}
              onChange={setQuestion}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Answer"
              value={answer}
              onChange={setAnswer}
              multiline={4}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={setDisplayOrder}
              autoComplete="off"
              helpText="Lower numbers appear first"
            />
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(value) => setStatus(value as ContentStatus)}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
